import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, Tag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { blogData } from '../data/mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogData.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = blogData
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 2);

  // Get next/prev posts
  const currentIndex = blogData.findIndex(p => p.id === post.id);
  const prevPost = currentIndex > 0 ? blogData[currentIndex - 1] : null;
  const nextPost = currentIndex < blogData.length - 1 ? blogData[currentIndex + 1] : null;

  const renderContent = (block, index) => {
    switch (block.type) {
      case 'paragraph':
        return (
          <p key={index} className="body-large text-[#b3b3b3] mb-6">
            {block.text}
          </p>
        );
      case 'heading':
        return (
          <h2 key={index} className="heading-2 text-white mt-10 mb-4">
            {block.text}
          </h2>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-3 mb-6 ml-4">
            {block.items.map((item, i) => (
              <li key={i} className="body-large text-[#b3b3b3] flex gap-3">
                <span className="text-[#c9a962] mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      {/* Back Link */}
      <div className="pt-24 md:pt-28 bg-[#0a0a0a]">
        <div className="container">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#c9a962] hover:gap-3 transition-all body-regular"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className="pt-8 pb-16 md:pb-20 bg-[#0a0a0a]">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-xs font-medium uppercase tracking-wider text-[#0a0a0a] bg-[#c9a962] px-3 py-1">
                {post.categoryLabel}
              </span>
              <span className="flex items-center gap-2 body-small text-[#808080]">
                <Calendar size={14} />
                {post.date}
              </span>
              <span className="flex items-center gap-2 body-small text-[#808080]">
                <Clock size={14} />
                {post.readTime}
              </span>
            </div>
            
            {/* Title */}
            <h1 className="hero-large text-white mb-6">
              {post.title}
            </h1>
            
            {/* Excerpt */}
            <p className="text-xl text-[#b3b3b3] mb-10 leading-relaxed">
              {post.excerpt}
            </p>
          </div>
          
          {/* Featured Image */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="aspect-[16/10] overflow-hidden border border-[#2a2a2a]">
              <img 
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover object-[center_20%]"
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="max-w-3xl mx-auto">
            {post.content.map((block, index) => renderContent(block, index))}
          </div>
          
          {/* CTA Section */}
          <div className="max-w-3xl mx-auto mt-16 p-8 bg-[#111111] border border-[#2a2a2a]">
            <h3 className="heading-2 text-white mb-4">
              Want Content That Actually Converts?
            </h3>
            <p className="body-large text-[#b3b3b3] mb-6">
              Let's create scroll-stopping content for your restaurant, café, hotel, or beauty clinic. I'd love to help your brand grow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                className="bg-[#c9a962] text-[#0a0a0a] hover:bg-[#d4b978] border-none rounded-none px-6 py-3 h-auto text-sm font-medium transition-all duration-300"
              >
                <Link to="/#portfolio">View My Portfolio</Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a] rounded-none px-6 py-3 h-auto text-sm font-medium transition-all duration-300"
              >
                <Link to="/#contact">Get in Touch</Link>
              </Button>
            </div>
          </div>

          {/* Post Navigation */}
          <div className="max-w-3xl mx-auto mt-12 pt-12 border-t border-[#2a2a2a]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {prevPost && (
                <Link 
                  to={`/blog/${prevPost.slug}`}
                  className="group p-6 bg-[#111111] border border-[#2a2a2a] hover:border-[#c9a962]/50 transition-all"
                >
                  <span className="body-small text-[#808080] flex items-center gap-2 mb-2">
                    <ArrowLeft size={14} />
                    Previous Article
                  </span>
                  <h4 className="heading-3 text-white group-hover:text-[#c9a962] transition-colors line-clamp-2">
                    {prevPost.title}
                  </h4>
                </Link>
              )}
              {nextPost && (
                <Link 
                  to={`/blog/${nextPost.slug}`}
                  className={`group p-6 bg-[#111111] border border-[#2a2a2a] hover:border-[#c9a962]/50 transition-all ${!prevPost ? 'md:col-start-2' : ''}`}
                >
                  <span className="body-small text-[#808080] flex items-center justify-end gap-2 mb-2">
                    Next Article
                    <ArrowRight size={14} />
                  </span>
                  <h4 className="heading-3 text-white group-hover:text-[#c9a962] transition-colors text-right line-clamp-2">
                    {nextPost.title}
                  </h4>
                </Link>
              )}
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-[#111111]">
          <div className="container">
            <h2 className="heading-1 text-white mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <div className="aspect-[16/10] overflow-hidden mb-4 border border-[#2a2a2a] image-hover">
                    <img 
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs font-medium uppercase tracking-wider text-[#c9a962]">
                    {relatedPost.categoryLabel}
                  </span>
                  <h3 className="heading-3 text-white mt-2 group-hover:text-[#c9a962] transition-colors">
                    {relatedPost.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
